import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { pontoApi, type DepartamentoPonto } from "@/features/ponto/pontoApi";

const VAZIO = { Id: 0, SNome: "", SCodigo: "", SSigla: "", IbAtivo: true };
const chave = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Cadastro de departamentos (setores) do RH — porta do FrmCadDepartamento. */
export default function DepartamentosPage() {
  const qc = useQueryClient();
  const { data: itens = [], isLoading } = useQuery({ queryKey: ["ponto-departamentos"], queryFn: () => pontoApi.departamentos(false) });
  const [busca, setBusca] = useState("");
  const [somenteAtivos, setSomenteAtivos] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(VAZIO);
  const [excluir, setExcluir] = useState<DepartamentoPonto | null>(null);

  const filtrados = useMemo(() => {
    const b = chave(busca.trim());
    return itens.filter((d) => (!somenteAtivos || d.ibAtivo)
      && (!b || chave(d.sNome).includes(b) || chave(d.sCodigo).includes(b) || chave(d.sSigla).includes(b)));
  }, [itens, busca, somenteAtivos]);
  const pag = usePaginacaoLocal(filtrados);

  const recarregar = () => {
    qc.invalidateQueries({ queryKey: ["ponto-departamentos"] });
    qc.invalidateQueries({ queryKey: ["ponto-funcionarios-lista"] });
  };

  const salvar = async () => {
    if (!form.SNome.trim()) { toast.warning("Informe o nome."); return; }
    try {
      await apiClient.post("/api/rh/ponto/departamentos", form);
      toast.success("Departamento salvo."); setDialog(false); recarregar();
    } catch (e: any) { toast.error(e.message ?? "Falha ao salvar."); }
  };

  const confirmarExclusao = async () => {
    if (!excluir) return;
    try {
      const r = await apiClient.delete(`/api/rh/ponto/departamentos/${excluir.id}`);
      toast.success(r.data?.mensagem ?? "Excluído."); setExcluir(null); recarregar();
    } catch (e: any) { toast.error(e.message ?? "Falha ao excluir."); }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Departamentos</h1>
          <p className="text-sm text-muted-foreground">Setores usados no cadastro de funcionários, nos filtros e no espelho em lote.</p>
        </div>
        <Button onClick={() => { setForm(VAZIO); setDialog(true); }}><Plus className="h-4 w-4 mr-2" />Novo</Button>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8" placeholder="Nome, código ou sigla" value={busca} onChange={(e) => setBusca(e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={somenteAtivos} onCheckedChange={setSomenteAtivos} id="dep-ativos" />
          <Label htmlFor="dep-ativos">Somente ativos</Label>
        </div>
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Código</TableHead>
              <TableHead>Sigla</TableHead>
              <TableHead className="text-right">Funcionários</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">Carregando…</TableCell></TableRow>
            ) : pag.itensPaginados.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">Nenhum departamento.</TableCell></TableRow>
            ) : pag.itensPaginados.map((d) => (
              <TableRow key={d.id} className={!d.ibAtivo ? "opacity-50" : undefined}>
                <TableCell>{d.sNome}{!d.ibAtivo && <Badge variant="outline" className="ml-2">inativo</Badge>}</TableCell>
                <TableCell>{d.sCodigo || "—"}</TableCell>
                <TableCell>{d.sSigla || "—"}</TableCell>
                <TableCell className="text-right">{d.qtdFuncionarios}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" aria-label="Editar" onClick={() => {
                    setForm({ Id: d.id, SNome: d.sNome, SCodigo: d.sCodigo, SSigla: d.sSigla, IbAtivo: d.ibAtivo }); setDialog(true);
                  }}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" aria-label="Excluir" onClick={() => setExcluir(d)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <PaginacaoRodape {...pag} onPaginaChange={pag.setPagina} onTamanhoPaginaChange={pag.setTamanhoPagina} />

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{form.Id ? "Editar departamento" : "Novo departamento"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1 col-span-2">
              <Label>Nome</Label>
              <Input value={form.SNome} maxLength={80} onChange={(e) => setForm({ ...form, SNome: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Código</Label>
              <Input value={form.SCodigo} maxLength={20} onChange={(e) => setForm({ ...form, SCodigo: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Sigla</Label>
              <Input value={form.SSigla} maxLength={10} onChange={(e) => setForm({ ...form, SSigla: e.target.value })} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.IbAtivo} onCheckedChange={(v) => setForm({ ...form, IbAtivo: v })} />
              <Label>Ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button onClick={salvar}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={excluir != null} onOpenChange={(o) => !o && setExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir o departamento {excluir?.sNome}?</AlertDialogTitle>
            <AlertDialogDescription>
              {excluir && excluir.qtdFuncionarios > 0
                ? `Há ${excluir.qtdFuncionarios} funcionário(s) nele: o departamento será apenas inativado.`
                : "O departamento será excluído."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmarExclusao}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
